import { useState, useEffect, useCallback, useRef } from 'react'
import useDebounce from '@/core/hooks/useDebounce/useDebounce'
import {
    FilterSection,
    SelectedFilterValues,
} from '../../FilterPopup/FilterPopup.types'
import { TGenericFilterType } from '@core/modules/filters'
import {
    FilterItem,
    UseSearchFilterSectionProps,
} from './useFilterSearchSection.types'
import { convertFiltersToActiveFilters } from './useFilterSearchSection.utils'

export const useSearchFilterSection = ({
    onSearchChange,
    onFilterChange,
    searchValue = '',
    filterConfig,
    getFilterOptions,
    activeFilters: externalActiveFilters,
    onFilterRemove,
    onFilterClick,
    onFilterReset,
    showFilterPopup = true,
}: UseSearchFilterSectionProps) => {
    const [localSearch, setLocalSearch] = useState(searchValue)
    const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false)
    const filterButtonRef = useRef<HTMLButtonElement>(null)
    const debouncedSearch = useDebounce(localSearch, 300)

    useEffect(() => {
        setLocalSearch(searchValue)
    }, [searchValue])

    useEffect(() => {
        if (debouncedSearch !== searchValue) {
            onSearchChange(debouncedSearch)
        }
    }, [debouncedSearch, searchValue, onSearchChange])

    const activeFilters =
        externalActiveFilters ||
        convertFiltersToActiveFilters(filterConfig, getFilterOptions)

    const filterSections: FilterSection[] = Object.entries(filterConfig).map(
        ([filterType, config]) => {
            const filterOptions = getFilterOptions(filterType)
            const currentOptions = filterOptions.activeOptions || []

            return {
                id: filterType,
                label: config.label,
                options: filterOptions.options.map((option: any) => ({
                    id: option.id,
                    label: option.label || option.id,
                    icon: option.icon,
                })),
                selectedOptionIds: currentOptions,
                withSearch: filterOptions.withSearch,
                collapsible:
                    filterOptions.collapsible ?? config.collapsible ?? true,
            }
        }
    )

    const handleFilterApply = useCallback(
        (selectedValues: SelectedFilterValues) => {
            Object.entries(selectedValues).forEach(([filterType, value]) => {
                const selectedOptions = value.selectedOptions.map(
                    (option) => option.id
                )
                onFilterChange(
                    filterType as TGenericFilterType,
                    selectedOptions
                )
            })
            setIsFilterPopupOpen(false)
        },
        [onFilterChange]
    )

    const handleFilterRemove = useCallback(
        (filterData: { filterName: string; filters: FilterItem[] }) => {
            if (onFilterRemove) {
                onFilterRemove(filterData)
            } else {
                const filterType = Object.keys(filterConfig).find(
                    (key) => filterConfig[key].label === filterData.filterName
                )
                if (filterType) {
                    onFilterChange(filterType as TGenericFilterType, [])
                }
            }
        },
        [onFilterRemove, onFilterChange, filterConfig]
    )

    const handleFilterReset = useCallback(() => {
        if (onFilterReset) {
            onFilterReset()
        } else {
            Object.keys(filterConfig).forEach((filterType) => {
                onFilterChange(filterType as TGenericFilterType, [])
            })
        }
        setIsFilterPopupOpen(false)
    }, [onFilterReset, onFilterChange, filterConfig])

    const handleFilterButtonClick = useCallback(() => {
        setIsFilterPopupOpen(!isFilterPopupOpen)
    }, [isFilterPopupOpen])

    const handleSearchChange = useCallback((value: string) => {
        setLocalSearch(value)
    }, [])

    const totalFilters = activeFilters.reduce(
        (total, filter) => total + filter.filters.length,
        0
    )
    const hasActiveFilters = totalFilters > 0
    const shouldShowFilterPopup = showFilterPopup && filterSections.length > 0

    return {
        // State
        localSearch,
        isFilterPopupOpen,
        filterButtonRef,
        activeFilters,
        filterSections,
        hasActiveFilters,
        totalFilters,

        // Handlers
        handleFilterApply,
        handleFilterRemove,
        handleFilterClick: onFilterClick,
        handleFilterReset,
        handleFilterButtonClick,
        handleFilterPopupClose: () => setIsFilterPopupOpen(false),
        handleSearchChange,

        // Computed values
        showFilterPopup: shouldShowFilterPopup,
    }
}
